<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useUserStore } from '../../stores/userStore'
import { useChatStore } from '../../stores/chatStore'
import { useConfigStore } from '../../stores/configStore'
import type { ChatAttachmentDto } from '../../dtos'
import { compressVideo, isVideoCompressionSupported } from '../../utils/videoCompressor'
import { compressAudio, isAudioCompressionSupported, isUncompressedAudio } from '../../utils/audioCompressor'

const props = defineProps<{
  bandId: number
  songId?: number
  label?: string
}>()

const userStore = useUserStore()
const chatStore = useChatStore()
const configStore = useConfigStore()

const newMessage = ref('')
const fileError = ref('')
const sending = ref(false)
const messagesContainer = ref<HTMLElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const selectedFile = ref<File | null>(null)
const filePreviewUrl = ref<string | null>(null)
const lightboxAttachment = ref<ChatAttachmentDto | null>(null)
const compressing = ref(false)
const compressProgress = ref(0)
let compressController: AbortController | null = null

const messages = computed(() => chatStore.messages)
const loading = computed(() => chatStore.loading)
const currentUserId = computed(() => userStore.dbUser?.id)

const allowedTypes = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/flac', 'audio/x-flac', 'audio/aiff', 'audio/x-aiff', 'audio/ogg', 'audio/webm', 'audio/mp4',
  'video/mp4', 'video/webm', 'video/quicktime'
]
const maxImageSize = 10 * 1024 * 1024
const maxMediaSize = 50 * 1024 * 1024
const maxVideoSizeBeforeCompression = 500 * 1024 * 1024

const canSend = computed(() => {
  return (newMessage.value.trim() || selectedFile.value) && !sending.value && !chatStore.uploading && !compressing.value
})

function scrollToBottom(smooth = false) {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTo({
        top: messagesContainer.value.scrollHeight,
        behavior: smooth ? 'smooth' : 'instant'
      })
    }
  })
}

function openFileDialog() {
  fileInput.value?.click()
}

async function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  fileError.value = ''

  // Validate type
  if (!allowedTypes.includes(file.type)) {
    fileError.value = 'Unsupported file type. Use images, audio, or video.'
    input.value = ''
    return
  }

  // Per-category size check (videos get a larger pre-compression cap — the
  // post-compression result still has to fit under maxMediaSize for upload).
  const isImage = file.type.startsWith('image/')
  const isVideo = file.type.startsWith('video/')
  if (isImage && file.size > maxImageSize) {
    fileError.value = 'Image too large. Max: 10 MB.'
    input.value = ''
    return
  }
  if (isVideo && file.size > maxVideoSizeBeforeCompression) {
    fileError.value = 'Video too large. Max: 500 MB.'
    input.value = ''
    return
  }
  if (!isImage && !isVideo && file.size > maxMediaSize) {
    fileError.value = 'Audio too large. Max: 50 MB.'
    input.value = ''
    return
  }

  selectedFile.value = file
  filePreviewUrl.value = isImage ? URL.createObjectURL(file) : null
  input.value = ''

  if (isVideo && isVideoCompressionSupported()) {
    await runVideoCompression(file)
  } else if (isUncompressedAudio(file) && isAudioCompressionSupported()) {
    await runAudioCompression(file)
  }
}

async function runVideoCompression(file: File) {
  compressController = new AbortController()
  compressing.value = true
  compressProgress.value = 0
  try {
    const compressed = await compressVideo(file, {
      maxHeight: 480,
      videoBitsPerSecond: 1_200_000,
      onProgress: (f) => { compressProgress.value = f },
      signal: compressController.signal,
    })
    // Only accept the result if the user hasn't cleared the file meanwhile.
    if (selectedFile.value !== file) return
    if (compressed.size > maxMediaSize) {
      fileError.value = `Compressed video is ${(compressed.size / 1024 / 1024).toFixed(1)} MB, over the 50 MB limit. Try a shorter clip.`
      clearFile()
      return
    }
    selectedFile.value = compressed
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return
    console.error('Video compression failed:', err)
    // Fall back to the original file if it fits under the upload limit.
    if (file.size <= maxMediaSize) {
      fileError.value = 'Compression failed; using original video.'
    } else {
      fileError.value = `Compression failed and original is over 50 MB. Cannot send.`
      clearFile()
    }
  } finally {
    compressing.value = false
    compressController = null
  }
}

async function runAudioCompression(file: File) {
  compressController = new AbortController()
  compressing.value = true
  compressProgress.value = 0
  try {
    const compressed = await compressAudio(file, {
      audioBitsPerSecond: 128_000,
      onProgress: (f) => { compressProgress.value = f },
      signal: compressController.signal,
    })
    if (selectedFile.value !== file) return
    if (compressed.size > maxMediaSize) {
      fileError.value = `Compressed audio is ${(compressed.size / 1024 / 1024).toFixed(1)} MB, over the 50 MB limit.`
      clearFile()
      return
    }
    selectedFile.value = compressed
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return
    console.error('Audio compression failed:', err)
    if (file.size <= maxMediaSize) {
      fileError.value = 'Compression failed; using original audio.'
    } else {
      fileError.value = 'Compression failed and original is over 50 MB. Cannot send.'
      clearFile()
    }
  } finally {
    compressing.value = false
    compressController = null
  }
}

function clearFile() {
  if (compressController) {
    compressController.abort()
    compressController = null
  }
  compressing.value = false
  compressProgress.value = 0
  if (filePreviewUrl.value) {
    URL.revokeObjectURL(filePreviewUrl.value)
  }
  selectedFile.value = null
  filePreviewUrl.value = null
}

function getMediaType(file: File): string {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('audio/')) return 'audio'
  return 'video'
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

async function handleSend() {
  const content = newMessage.value.trim()
  const file = selectedFile.value

  if ((!content && !file) || sending.value || chatStore.uploading) return

  sending.value = true
  newMessage.value = ''
  const savedContent = content

  try {
    if (file) {
      await chatStore.sendMessageWithAttachment(content, file)
      clearFile()
    } else {
      await chatStore.sendMessage(content)
    }
    scrollToBottom(true)
  } catch {
    newMessage.value = savedContent
  } finally {
    sending.value = false
  }
}

async function handleLoadOlder() {
  const container = messagesContainer.value
  const prevHeight = container?.scrollHeight ?? 0
  await chatStore.loadOlderMessages()
  nextTick(() => {
    if (container) {
      container.scrollTop = container.scrollHeight - prevHeight
    }
  })
}

function formatTime(dateStr: string) {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  if (diffDays === 0) return time
  if (diffDays === 1) return `Yesterday ${time}`
  if (diffDays < 7) return `${date.toLocaleDateString([], { weekday: 'short' })} ${time}`
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`
}

function getInitial(name?: string) {
  return (name || '?').charAt(0).toUpperCase()
}

const showDeleteConfirm = ref(false)
const messageToDelete = ref<number | null>(null)
const editingMessageId = ref<number | null>(null)
const editContent = ref('')

function confirmDeleteMessage(messageId: number) {
  messageToDelete.value = messageId
  showDeleteConfirm.value = true
}

async function handleDeleteMessage() {
  if (!messageToDelete.value) return
  try {
    await chatStore.deleteMessage(messageToDelete.value)
  } catch {
    // error logged in store
  }
  showDeleteConfirm.value = false
  messageToDelete.value = null
}

function startEditing(msg: { id: number; content: string }) {
  editingMessageId.value = msg.id
  editContent.value = msg.content
}

function cancelEditing() {
  editingMessageId.value = null
  editContent.value = ''
}

async function saveEdit() {
  if (!editingMessageId.value || !editContent.value.trim()) return
  try {
    await chatStore.editMessage(editingMessageId.value, editContent.value.trim())
  } catch {
    // error logged in store
  }
  editingMessageId.value = null
  editContent.value = ''
}

// URL detection and linkify
type MessageSegment = { type: 'text'; value: string } | { type: 'link'; value: string }

const urlRegex = /(https?:\/\/[^\s<]+)/g

function parseMessageContent(content: string): MessageSegment[] {
  const segments: MessageSegment[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  urlRegex.lastIndex = 0
  while ((match = urlRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, match.index) })
    }
    segments.push({ type: 'link', value: match[1]! })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) })
  }
  return segments
}

function getYouTubeVideoId(url: string): string | null {
  try {
    const u = new URL(url)
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('/')[0] || null
    if (u.hostname.includes('youtube.com') && u.searchParams.has('v')) return u.searchParams.get('v')
    if (u.hostname.includes('youtube.com') && u.pathname.startsWith('/shorts/')) return u.pathname.split('/')[2] || null
  } catch { /* not a valid url */ }
  return null
}

function getMessageLinks(content: string): string[] {
  urlRegex.lastIndex = 0
  return content.match(urlRegex) || []
}

function openLightbox(att: ChatAttachmentDto) {
  lightboxAttachment.value = att
}

function closeLightbox() {
  lightboxAttachment.value = null
}

async function init() {
  await chatStore.fetchMessages(props.bandId, props.songId)
  chatStore.startPolling(props.bandId, props.songId)
  scrollToBottom()
}

watch([() => props.bandId, () => props.songId], () => {
  init()
}, { immediate: true })

watch(() => chatStore.newMessageFlag, () => {
  scrollToBottom(true)
})

onUnmounted(() => {
  chatStore.clear()
  if (filePreviewUrl.value) {
    URL.revokeObjectURL(filePreviewUrl.value)
  }
})
</script>

<template>
  <div>
    <p class="rd-label mb-3">{{ label || 'Chat' }}</p>

    <div class="rd-chat-container">
      <!-- Messages -->
      <div
        ref="messagesContainer"
        class="h-64 overflow-y-auto p-3 md:px-16 md:py-4 space-y-2 bg-white chat-messages-bg"
      >
        <!-- Load older -->
        <div v-if="chatStore.hasMore && messages.length > 0" class="text-center">
          <button
            class="text-[10px] text-teal-600 hover:text-teal-800 font-medium"
            @click="handleLoadOlder"
          >Load older messages</button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="flex justify-center py-8">
          <ProgressSpinner style="width: 24px; height: 24px" strokeWidth="6" />
        </div>

        <!-- Empty state -->
        <div v-else-if="messages.length === 0" class="flex flex-col items-center justify-center h-full text-slate-400">
          <i class="pi pi-comments text-2xl mb-2"></i>
          <p class="text-xs">No messages yet. Say hello!</p>
        </div>

        <!-- Message bubbles -->
        <template v-else>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="flex gap-2"
            :class="msg.userId === currentUserId ? 'flex-row-reverse' : ''"
          >
            <!-- Avatar (other users only) -->
            <img
              v-if="msg.userId !== currentUserId && msg.profilePicture"
              :src="msg.profilePicture"
              class="w-6 h-6 rounded-full object-cover shrink-0"
              @error="() => { msg.profilePicture = undefined }"
            />
            <div
              v-else-if="msg.userId !== currentUserId"
              class="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center shrink-0 text-[10px] font-bold text-slate-500"
            >
              {{ getInitial(msg.displayName) }}
            </div>

            <!-- Bubble -->
            <div class="max-w-[75%]">
              <div
                v-if="msg.userId !== currentUserId"
                class="text-[9px] font-semibold text-slate-400 mb-0.5 ml-1"
              >
                {{ msg.displayName || 'Unknown' }}
              </div>

              <!-- Inline edit mode -->
              <div v-if="editingMessageId === msg.id" class="flex flex-col gap-1">
                <input
                  v-model="editContent"
                  type="text"
                  class="rd-input text-sm px-3 py-1.5 rounded-2xl"
                  @keyup.enter="saveEdit"
                  @keyup.escape="cancelEditing"
                />
                <div class="flex gap-1 justify-end">
                  <button class="text-[10px] text-slate-400 hover:text-slate-600 px-2 py-0.5" @click="cancelEditing">Cancel</button>
                  <button class="text-[10px] text-teal-600 hover:text-teal-800 font-bold px-2 py-0.5" @click="saveEdit">Save</button>
                </div>
              </div>

              <!-- Normal display mode -->
              <template v-else>
                <div class="flex items-center gap-3">
                  <div
                    class="px-3 py-1.5 rounded-2xl text-sm leading-relaxed break-words"
                    :class="msg.userId === currentUserId
                      ? 'bg-teal-500 text-white rounded-br-md'
                      : 'bg-white border border-slate-400 text-slate-700 rounded-bl-md'"
                  >
                    <span v-if="msg.content">
                      <template v-for="(seg, i) in parseMessageContent(msg.content)" :key="i">
                        <span v-if="seg.type === 'text'">{{ seg.value }}</span>
                        <a
                          v-else
                          :href="seg.value"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="underline break-all"
                          :class="msg.userId === currentUserId ? 'text-white/90 hover:text-white' : 'text-teal-600 hover:text-teal-800'"
                          @click.stop
                        >{{ seg.value }}</a>
                      </template>
                    </span>

                    <!-- YouTube previews -->
                    <template v-if="msg.content">
                      <div
                        v-for="link in getMessageLinks(msg.content)"
                        :key="'yt-' + link"
                      >
                        <a
                          v-if="getYouTubeVideoId(link)"
                          :href="link"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="block mt-1.5 rounded-lg overflow-hidden relative group"
                          @click.stop
                        >
                          <img
                            :src="`https://img.youtube.com/vi/${getYouTubeVideoId(link)}/mqdefault.jpg`"
                            class="w-full max-w-[280px] rounded-lg"
                            loading="lazy"
                          />
                          <div class="absolute inset-0 flex items-center justify-center">
                            <div class="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:bg-red-700 transition-colors">
                              <i class="pi pi-play-circle text-white text-lg"></i>
                            </div>
                          </div>
                        </a>
                      </div>
                    </template>

                    <!-- Attachments -->
                    <template v-if="msg.attachments?.length">
                      <div v-for="att in msg.attachments" :key="att.id" class="mt-1.5">
                        <!-- Image -->
                        <img
                          v-if="att.mediaType === 'image'"
                          :src="att.filePath"
                          :alt="att.fileName"
                          loading="lazy"
                          class="rounded-lg max-w-full max-h-48 cursor-pointer"
                          @click="openLightbox(att)"
                        />
                        <!-- Audio -->
                        <div v-else-if="att.mediaType === 'audio'" class="min-w-[200px]">
                          <div class="flex items-center gap-1.5 mb-1 text-xs opacity-75">
                            <i class="pi pi-volume-up text-[10px]"></i>
                            <span class="truncate">{{ att.fileName }}</span>
                            <span class="text-[10px]">({{ formatFileSize(att.fileSize) }})</span>
                          </div>
                          <audio :src="att.filePath" controls class="w-full h-8" preload="metadata" />
                        </div>
                        <!-- Video -->
                        <video
                          v-else-if="att.mediaType === 'video'"
                          :src="att.filePath"
                          controls
                          class="rounded-lg max-w-full max-h-48"
                          preload="metadata"
                        />
                      </div>
                    </template>
                  </div>
                  <div v-if="msg.userId === currentUserId" class="flex items-center gap-2.5 shrink-0">
                    <button
                      class="w-5 h-5 rounded-full flex items-center justify-center text-slate-500 hover:text-teal-600 hover:bg-slate-100 transition-colors"
                      @click.stop="startEditing(msg)"
                    >
                      <i class="pi pi-pencil text-[9px]"></i>
                    </button>
                    <button
                      class="w-5 h-5 rounded-full flex items-center justify-center text-slate-500 hover:text-red-500 hover:bg-slate-100 transition-colors"
                      @click.stop="confirmDeleteMessage(msg.id)"
                    >
                      <i class="pi pi-trash text-[9px]"></i>
                    </button>
                  </div>
                </div>
                <div
                  class="rd-timestamp mt-0.5"
                  :class="msg.userId === currentUserId ? 'text-right mr-1' : 'ml-1'"
                >
                  {{ formatTime(msg.createdAt) }}
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>

      <!-- File error -->
      <div v-if="fileError" class="px-3 py-1.5 bg-red-50 text-red-600 text-xs flex items-center justify-between">
        <span>{{ fileError }}</span>
        <button class="ml-2 hover:text-red-800" @click="fileError = ''"><i class="pi pi-times text-[10px]"></i></button>
      </div>

      <!-- File preview strip -->
      <div v-if="selectedFile" class="rd-divider px-3 py-2 flex items-center gap-2 bg-slate-50">
        <!-- Image thumbnail -->
        <img
          v-if="filePreviewUrl"
          :src="filePreviewUrl"
          class="h-10 w-10 rounded object-cover"
        />
        <!-- Non-image icon -->
        <div v-else class="h-10 w-10 rounded bg-slate-200 flex items-center justify-center">
          <i :class="getMediaType(selectedFile) === 'audio' ? 'pi pi-volume-up' : 'pi pi-video'" class="text-slate-500"></i>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium text-slate-700 truncate">{{ selectedFile.name }}</p>
          <template v-if="compressing">
            <p class="text-[10px] text-teal-600">Compressing video… {{ Math.round(compressProgress * 100) }}%</p>
            <div class="h-1 bg-slate-200 rounded-full overflow-hidden mt-1">
              <div class="h-full bg-teal-500 transition-all" :style="{ width: `${compressProgress * 100}%` }"></div>
            </div>
          </template>
          <p v-else class="text-[10px] text-slate-400">{{ formatFileSize(selectedFile.size) }}</p>
        </div>
        <button
          class="w-6 h-6 rounded-full hover:bg-slate-200 flex items-center justify-center transition-colors"
          @click="clearFile"
        >
          <i class="pi pi-times text-xs text-slate-500"></i>
        </button>
      </div>

      <!-- Input -->
      <div class="rd-divider p-2 flex gap-2">
        <template v-if="configStore.chatMediaUploadsEnabled">
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            accept="image/jpeg,image/png,image/gif,image/webp,audio/mpeg,audio/wav,audio/x-wav,audio/wave,audio/flac,audio/x-flac,audio/aiff,audio/x-aiff,audio/ogg,audio/webm,audio/mp4,video/mp4,video/webm,video/quicktime"
            @change="handleFileSelect"
          />
          <button
            class="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors shrink-0"
            :disabled="chatStore.uploading"
            @click="openFileDialog"
          >
            <i class="pi pi-paperclip text-sm text-slate-500"></i>
          </button>
        </template>
        <input
          v-model="newMessage"
          type="text"
          placeholder="Type a message..."
          class="flex-1 min-w-0 rd-input"
          :disabled="chatStore.uploading"
          @keyup.enter="handleSend"
        />
        <button
          class="w-8 h-8 rounded-full bg-teal-500 text-white flex items-center justify-center hover:bg-teal-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          :disabled="!canSend"
          @click="handleSend"
        >
          <i v-if="chatStore.uploading" class="pi pi-spin pi-spinner text-xs"></i>
          <i v-else class="pi pi-send text-xs"></i>
        </button>
      </div>
    </div>

    <!-- Image lightbox -->
    <div
      v-if="lightboxAttachment"
      class="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      @click="closeLightbox"
    >
      <button
        class="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
        @click="closeLightbox"
      >
        <i class="pi pi-times text-white text-lg"></i>
      </button>
      <img
        :src="lightboxAttachment.filePath"
        :alt="lightboxAttachment.fileName"
        class="max-w-full max-h-full object-contain rounded-lg"
        @click.stop
      />
    </div>

    <!-- Delete Message Confirm Dialog -->
    <Dialog v-model:visible="showDeleteConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-trash text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Delete Message?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This message will be permanently deleted.</p>
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Delete" severity="danger" class="w-full py-3 font-bold" @click="handleDeleteMessage" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDeleteConfirm = false" />
        </div>
      </div>
    </Dialog>
  </div>
</template>
